import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { CommonModule, DecimalPipe } from "@angular/common";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { SharedModule } from "../../../../shared/shared.module";
import { HeaderDialogComponent } from "../../../../shared/modules/dialog/header-dialog/header-dialog.component";
import { ExcelExportButtonComponent } from "../../../../shared/components/excel-export-button/excel-export-button.component";
import { RadioCardComponent } from "../../../../shared/components/radio-card/radio-card.component";
import { NC_SUPPLIER_COMPLIANCE_DATA, SupplierComplianceBucket } from "../../models/data.model";
import { exportNcSupplierComplianceExcel } from "./nc-supplier-compliance-excel.utils";
import { NonConformiteApiService } from "../../../non-conformite/services/non-conformite/non-conformite-api.service";
import {
  ExcelExportConfirmDialogComponent,
  ExcelExportConfirmData,
  ExcelExportConfirmResult,
} from "../../../../shared/components/excel-export-confirm-dialog/excel-export-confirm-dialog.component";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";

export interface NcSupplierComplianceDialogData {
  data: NC_SUPPLIER_COMPLIANCE_DATA;
  /** Bornes de période actives — utilisées pour recharger en mode detailed avant l'export. */
  start?: string;
  end?: string;
}

@Component({
  selector: "vl-nc-supplier-compliance-dialog",
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    HeaderDialogComponent,
    DecimalPipe,
    ExcelExportButtonComponent,
    RadioCardComponent,
  ],
  templateUrl: "./nc-supplier-compliance-dialog.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NcSupplierComplianceDialogComponent {
  data = inject<NcSupplierComplianceDialogData>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<NcSupplierComplianceDialogComponent>);

  exporting = signal(false);

  worst = computed<SupplierComplianceBucket[]>(() => this.data.data?.worst ?? []);
  best = computed<SupplierComplianceBucket[]>(() => this.data.data?.best ?? []);

  /** KPI synthèse pour les radio-cards. */
  totalFournisseurs = computed(() => this.worst().length + this.best().length);
  totalNc = computed(() => [...this.worst(), ...this.best()].reduce((s, b) => s + b.nbNc, 0));
  totalCritiques = computed(() =>
    [...this.worst(), ...this.best()].reduce((s, b) => s + b.nbNcCritiques, 0),
  );
  moyTauxBest = computed(() => {
    const arr = this.best();
    if (!arr.length) return null;
    return Math.round(
      (arr.reduce((s, b) => s + (b.tauxConformite ?? 0), 0) / arr.length) * 10,
    ) / 10;
  });

  fmtTaux(v: number | null): string {
    return v == null ? "—" : `${v}%`;
  }

  tauxColor(taux: number | null): string {
    if (taux == null) return "var(--color-on-disabled)";
    if (taux < 90) return "var(--color-error)";
    if (taux < 95) return "var(--color-warning)";
    return "var(--color-valid)";
  }

  private ncApi = inject(NonConformiteApiService);
  private dialogs = inject(DialogsService);

  async exportExcel() {
    // Étape 1 : confirmation + override période optionnel
    const ref = this.dialogs.open(
      ExcelExportConfirmDialogComponent,
      "fit-content",
      "fit-content",
      {
        title: "Exporter — Conformité fournisseurs",
        subtitle:
          this.data.start && this.data.end
            ? `Plage active : ${this.fmtPeriod(this.data.start)} → ${this.fmtPeriod(this.data.end)}`
            : undefined,
        allowCustomRange: true,
        initialCustomStart: this.data.start,
        initialCustomEnd: this.data.end,
      } as ExcelExportConfirmData,
    );
    const confirm = (await firstValueFrom(ref.afterClosed())) as
      | ExcelExportConfirmResult
      | null;
    if (!confirm) return;

    this.exporting.set(true);
    try {
      const start = confirm.customStart ?? this.data.start;
      const end = confirm.customEnd ?? this.data.end;
      // Recharge en mode `detailed` pour récupérer la liste exhaustive des
      // fournisseurs + détail articles + détail NC type RECEPTION sur la
      // période sélectionnée.
      const detailed = await firstValueFrom(
        this.ncApi.supplierCompliance(start, end, undefined, 100, true),
      );
      await exportNcSupplierComplianceExcel(detailed);
    } finally {
      this.exporting.set(false);
    }
  }

  private fmtPeriod(iso: string): string {
    try {
      return new Date(iso).toLocaleDateString("fr-FR");
    } catch {
      return iso;
    }
  }
}
